/**
 * RealtorSidebar - Sticky sidebar showing realtor/agent information
 *
 * Features:
 * - Agent photo (circular)
 * - Organization name
 * - Verification badge
 * - Contact information
 * - CTA button
 */
import { Mail, Phone, CheckCircle } from "lucide-react";
import { Card } from "@/shared/ui/components";
import { Button } from "@/shared/ui/components";
import { useTranslation } from "@/shared/hooks";

interface RealtorSidebarProps {
  realtor: {
    org_id: string;
    org_name: string;
    org_type: string;
    contact_email?: string | null;
    contact_phone?: string | null;
    verification_status: string;
    agent_photo_url?: string | null;
  };
}

export function RealtorSidebar({ realtor }: RealtorSidebarProps) {
  const t = useTranslation();
  const isVerified = realtor.verification_status === "verified";

  const handleContactClick = () => {
    if (realtor.contact_email) {
      window.location.href = `mailto:${realtor.contact_email}?subject=Inquiry about property`;
    }
  };

  return (
    <Card variant="default" padding="lg" className="shadow-sm">
      <h3 className="text-lg font-semibold text-gray-900 mb-4">
        {t("listingDetailListedBy")}
      </h3>

      {/* Agent Photo & Info - Left Aligned */}
      <div className="flex items-start gap-4 mb-6">
        {/* Agent Photo */}
        {realtor.agent_photo_url && (
          <img
            src={realtor.agent_photo_url}
            alt={realtor.org_name}
            className="w-16 h-16 rounded-full object-cover border-4 border-primary-100 flex-shrink-0"
          />
        )}

        {/* Organization Info */}
        <div className="flex-1 min-w-0">
          {/* Organization Name */}
          <h4 className="text-lg font-semibold text-gray-900 mb-1 truncate">
            {realtor.org_name}
          </h4>

          {/* Verification Badge */}
          {isVerified && (
            <div className="flex items-center gap-1.5 text-green-600 text-sm mb-1">
              <CheckCircle className="w-4 h-4" />
              <span className="font-medium">
                {t("listingDetailVerifiedPartner")}
              </span>
            </div>
          )}

          {/* Organization Type */}
          <p className="text-sm text-gray-500 capitalize">
            {realtor.org_type.replace(/_/g, " ")}
          </p>
        </div>
      </div>

      {/* Contact Information */}
      <div className="space-y-3 pt-4 border-t border-gray-200 mb-6">
        {realtor.contact_email && (
          <div className="flex items-start gap-2 text-sm">
            <Mail className="w-4 h-4 text-gray-400 mt-0.5 flex-shrink-0" />
            <a
              href={`mailto:${realtor.contact_email}`}
              className="text-primary-600 hover:text-primary-700 hover:underline break-all"
            >
              {realtor.contact_email}
            </a>
          </div>
        )}

        {realtor.contact_phone && (
          <div className="flex items-start gap-2 text-sm">
            <Phone className="w-4 h-4 text-gray-400 mt-0.5 flex-shrink-0" />
            <a
              href={`tel:${realtor.contact_phone}`}
              className="text-primary-600 hover:text-primary-700 hover:underline"
            >
              {realtor.contact_phone}
            </a>
          </div>
        )}
      </div>

      {/* Contact CTA Button */}
      <Button
        variant="primary"
        className="w-full"
        onClick={handleContactClick}
        disabled={!realtor.contact_email}
      >
        <Mail className="w-4 h-4 mr-2" />
        {t("listingDetailContactAgent")}
      </Button>
    </Card>
  );
}
